import { ReactNode } from 'react';
import { Link } from 'react-router';
import { Box, Button, Skeleton, Stack, Typography } from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { PercentChange } from './period';
import { StatDelta } from './StatDelta';

interface Props {
  label: ReactNode;
  value: ReactNode;
  change?: PercentChange;
  /** Whether an increase is a good thing (drives the delta chip colour). Defaults to true. */
  higherIsBetter?: boolean;
  caption?: ReactNode;
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: ReactNode;
  retryLabel?: ReactNode;
  onRetry?: () => void;
  onClick?: () => void;
  selected?: boolean;
  /** id of the detail panel this tile expands, for aria-controls. */
  controls?: string;
  to?: string;
}

export const KeyFigureTile = ({
  label,
  value,
  change,
  higherIsBetter = true,
  caption,
  isLoading,
  isError,
  errorMessage,
  retryLabel,
  onRetry,
  onClick,
  selected = false,
  controls,
  to,
}: Props) => {
  const disabled = Boolean(isError) || Boolean(isLoading);
  const isLink = Boolean(to) && !disabled;
  const interactive = (Boolean(onClick) || isLink) && !disabled;

  const componentProps = isLink
    ? { component: Link, to }
    : {
        component: interactive ? 'button' : 'div',
        type: interactive ? 'button' : undefined,
        onClick: interactive ? onClick : undefined,
        'aria-expanded': interactive ? selected : undefined,
        'aria-controls': interactive && selected ? controls : undefined,
      };

  return (
    <Box
      {...componentProps}
      sx={{
        textDecoration: 'none',
        flex: '1 1 200px',
        minWidth: 0,
        m: 0,
        p: 2,
        textAlign: 'left',
        font: 'inherit',
        color: 'inherit',
        border: 1,
        borderColor: selected ? 'primary.main' : 'divider',
        borderRadius: 2,
        backgroundColor: 'background.paper',
        cursor: interactive ? 'pointer' : 'default',
        transition: 'border-color 120ms, background-color 120ms',
        ...(interactive && {
          '&:hover': { borderColor: 'primary.main', backgroundColor: 'action.hover' },
          '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
        }),
      }}
    >
      <Stack gap={0.5} sx={{ minWidth: 0 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
          <Typography variant="body2" color="text.secondary" noWrap>
            {label}
          </Typography>
          {interactive &&
            (isLink ? (
              <ArrowForwardRoundedIcon
                fontSize="small"
                sx={{ color: 'text.disabled', flexShrink: 0 }}
              />
            ) : (
              <ExpandMoreRoundedIcon
                fontSize="small"
                sx={{
                  color: 'text.disabled',
                  flexShrink: 0,
                  transform: selected ? 'rotate(180deg)' : 'none',
                  transition: 'transform 150ms',
                }}
              />
            ))}
        </Stack>
        {isError ? (
          <Stack alignItems="center">
            <Stack direction="row" alignItems="center" gap={1}>
              <ErrorOutlineRoundedIcon color="error" fontSize="small" />
              <Typography variant="body2" color="text.secondary">
                {errorMessage}
              </Typography>
            </Stack>
            {onRetry && (
              <Box>
                <Button
                  size="small"
                  variant="text"
                  color="inherit"
                  startIcon={<RefreshRoundedIcon />}
                  onClick={onRetry}
                >
                  {retryLabel}
                </Button>
              </Box>
            )}
          </Stack>
        ) : (
          <>
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
              {isLoading ? (
                <Skeleton variant="rounded" width={96} height={34} />
              ) : (
                <Typography variant="h4" fontWeight={700} lineHeight={1.1}>
                  {value}
                </Typography>
              )}
              {!isLoading && change && <StatDelta change={change} higherIsBetter={higherIsBetter} />}
            </Stack>
            {caption && !isLoading && (
              <Typography variant="caption" color="text.secondary">
                {caption}
              </Typography>
            )}
          </>
        )}
      </Stack>
    </Box>
  );
};
